import { useState } from 'react'
import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '../helpers/sessionStorage'
import getIsTWAMode from '../helpers/twaMode'

// Extracted from the retired ActivityCloseButton (its white X control is
// superseded by ExperienceNavControls' kids-return pill): resolves the
// `returnTo` query param the discovery-kids app appends to in-WebView
// activity links, pointing back to its Activities tab, and owns the
// closeActivity exit semantics that pill uses.
//
// The Together Android app also appends `returnTo` when it opens a Discovery
// Center entry link from a QR scan, so an activity can be the very first page
// in the tab with nothing behind it to pop back to. Inside that app the level
// above every experience is always the Activities tab, so when a page there
// arrives without the param (a link tapped inside the WebView, an app build
// that does not append it) the destination defaults to that tab rather than
// leaving the visitor with no way out.

const isHttpUrl = (url) => url.protocol === 'http:' || url.protocol === 'https:'

// Local TWA development runs kids and react on different localhost ports, so
// a same-origin-only guard would reject every returnTo. A single explicit
// origin can be allowed via env, in development builds only.
const normalizeAllowedReturnOrigin = (value) => {
  if (typeof value !== 'string' || value.includes('\\')) return undefined

  try {
    const url = new URL(value)
    if (!isHttpUrl(url) || url.username || url.password) return undefined
    return url.origin
  } catch {
    return undefined
  }
}

const allowedReturnOrigin =
  process.env.NODE_ENV === 'development'
    ? normalizeAllowedReturnOrigin(process.env.REACT_APP_ALLOWED_RETURN_ORIGIN)
    : undefined

const toSafeReturnPath = (value) => {
  if (typeof value !== 'string' || value.includes('\\')) return null

  if (allowedReturnOrigin) {
    try {
      const url = new URL(value)
      if (isHttpUrl(url) && !url.username && !url.password && url.origin === allowedReturnOrigin) {
        // Rebuilt from the configured origin rather than returned as received, so the value that reaches
        // the navigation sink never starts with anything the URL param controlled.
        return `${allowedReturnOrigin}${url.pathname}${url.search}${url.hash}`
      }
    } catch {
      // Continue to the relative-path guard below.
    }
  }

  if (!value.startsWith('/') || value.startsWith('//')) {
    return null
  }
  try {
    const resolved = new URL(value, window.location.origin)
    if (resolved.origin !== window.location.origin) return null
    // Dot segments survive the raw check above but resolve away: '/.//evil.com' and
    // '/%2e//evil.com' both come back with the pathname '//evil.com', which a navigation
    // sink would read as protocol-relative. Check the path that is actually returned.
    if (resolved.pathname.startsWith('//')) return null
    return `${resolved.pathname}${resolved.search}${resolved.hash}`
  } catch {
    return null
  }
}

const RETURN_TO_STORAGE_KEY = 'activityReturnTo'
// Mirrors TOGETHER_RETURN_TO_VALUE in the Together Android app (CenterEntryUrlUtil).
const TOGETHER_ACTIVITIES_PATH = '/discovery/together/activities'
const ENTRY_HISTORY_LENGTH_KEY = 'activityEntryHistoryLength'

// Whether this document is the activity's entry document. Decided once per document by
// captureActivityEntry; false until it has run.
let entryDocument = false

/**
 * Whether there is anything behind the activity to pop back to. If not, closing has to be a real
 * navigation.
 */
function openedFromAnotherPage() {
  try {
    const entryLength = Number(getSessionStorageItem(ENTRY_HISTORY_LENGTH_KEY))
    return Number.isInteger(entryLength) && entryLength >= 2
  } catch {
    return false
  }
}

/**
 * Whether this document started at the activity's entry point rather than being a reload deeper in
 * the activity. ActivityExitProvider uses it to decide whether the entries it counted since mounting
 * are the whole visit (known depth) or whether the depth is unknown and closing must navigate outright.
 */
export function startedAtActivityEntry() {
  return entryDocument
}

/**
 * Forgets where the current activity's entry sat in the tab's history. Anything that ends an activity
 * must call it, so the next activity opened in this tab measures its own entry instead of inheriting it.
 */
export const clearActivityEntry = () => {
  try {
    removeSessionStorageItem(RETURN_TO_STORAGE_KEY)
    removeSessionStorageItem(ENTRY_HISTORY_LENGTH_KEY)
  } catch {
    // Leaving a stale record behind is the worst case, and the next fresh navigation overwrites it.
  }
}

/**
 * Leaves the activity the way Android's back button would.
 *
 * `depth` is how many entries the activity pushed after it opened, tracked by ActivityExitProvider.
 *
 * Falls back to a real navigation when there is nothing behind us, or when the depth is unknown.
 *
 * The activity is over either way, so its entry record is cleared: the next activity opened in this tab
 * must measure its own entry instead of inheriting where this one sat in the history.
 */
export function closeActivity(returnTo, depth = 0) {
  if (Number.isInteger(depth) && openedFromAnotherPage()) {
    clearActivityEntry()
    window.history.go(-(depth + 1))
    return
  }

  const safeReturnTo = toSafeReturnPath(returnTo)
  if (!safeReturnTo) {
    throw new TypeError('Cannot close activity: returnTo must be a safe return URL')
  }

  clearActivityEntry()
  window.location.assign(safeReturnTo)
}

// How many entries the tab holds up to and including this one. history.length also counts entries
// ahead of it - after going back to the entry page and reloading it, the page it went back from is
// still ahead - and popping back toward those would go nowhere. The Navigation API reports the actual
// position; browsers without it fall back to history.length.
const entriesThroughCurrent = () => {
  const index = window.navigation?.currentEntry?.index
  return Number.isInteger(index) ? index + 1 : window.history.length
}

const readReturnToParam = () => {
  try {
    return new URLSearchParams(window.location.search).get('returnTo')
  } catch {
    return null
  }
}

/**
 * Records where the activity's entry document sits in the tab's history. Call once at document
 * start, before the router runs. This is the only writer of the entry record.
 *
 * The history length has to be captured before the activity pushes anything of its own. A Discovery
 * Center QR launch lands on the welcome page (LoginPage), whose signed-in effect pushes the onboarding
 * route before any step mounts, so by the time a step mounts the length already counts that push and
 * closeActivity would pop back to the welcome page - which pushes onboarding again, so the close
 * control appears to do nothing. Measured at document start the length is still 1 and closing falls
 * back to a real navigation to returnTo.
 *
 * The presence of returnTo does not make this document the entry: LoginPage and the onboarding steps
 * forward the whole query string, so it rides along on every URL the activity pushes. What tells a
 * reload deeper in the activity (pull-to-refresh, memory-pressure restore) apart from a fresh
 * navigation into it is history.state - the router stamps state onto every entry it pushes, while a
 * fresh navigation arrives with none. A reload therefore records nothing, so the length measured when
 * the activity was actually opened stands, and the depth is reported as unknown so closing navigates
 * outright.
 */
export function captureActivityEntry() {
  entryDocument = false
  if (typeof window === 'undefined') return

  const fromUrl = readReturnToParam()
  if (fromUrl === null) return

  const safe = toSafeReturnPath(fromUrl)
  if (!safe) return

  try {
    if (window.history.state != null) return

    setSessionStorageItem({ [RETURN_TO_STORAGE_KEY]: safe, [ENTRY_HISTORY_LENGTH_KEY]: entriesThroughCurrent() })
    entryDocument = true
  } catch {
    // sessionStorage itself is unreachable in some locked-down webviews. Nothing is recorded, so
    // openedFromAnotherPage() stays false and closing falls back to a real navigation. A write that
    // the helper drops silently (quota) ends up in the same place, since nothing re-measures later.
  }
}

/**
 * The URL param is authoritative when present - an unsafe one resolves to null rather than falling
 * back - otherwise the copy captureActivityEntry stored at document start, which keeps the
 * kids-return control visible after in-activity navigation stripped the param from the URL.
 * With neither, a page inside the Together app still returns to the Activities tab (DSC-9991).
 * A bare `returnTo=` counts as absent, as it does for the Android launcher that fills it in.
 */
const readReturnTo = () => {
  const fromUrl = readReturnToParam()
  if (fromUrl !== null && fromUrl !== '') return toSafeReturnPath(fromUrl)

  let stored = null
  try {
    stored = toSafeReturnPath(getSessionStorageItem(RETURN_TO_STORAGE_KEY))
  } catch {
    // sessionStorage is unreachable in some locked-down webviews; fall through to the default.
  }
  if (stored !== null) return stored

  return getIsTWAMode() ? TOGETHER_ACTIVITIES_PATH : null
}

/**
 * Resolves the returnTo path once on mount, or null when there is none.
 *
 * Only same-origin paths survive (open-redirect guard, plus the dev-only allowed
 * origin above) - anything else resolves to null.
 */
export default function useActivityReturnTo() {
  const [returnTo] = useState(() => readReturnTo())
  return returnTo
}
